<template>
  <div class="cr-page tk-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <CreateCampaignDialog
      v-model="showCreateDialog"
      @saved="onCampaignCreated"
      @created="onCampaignCreatedGoToSettings"
      @error="notifyError"
    />

    <CreateCampaignDialog
      v-model="showEditDialog"
      :campaign="editingCampaign"
      @saved="onCampaignEdited"
      @error="notifyError"
    />

    <CampaignConfirmDialog
      v-model="confirmDialogOpen"
      :action="confirmDialogAction"
      :campaign="confirmDialogCampaign"
      :confirm-fn="confirmDialogFn"
      @confirmed="onConfirmDialogConfirmed"
      @error="notifyError"
    />

    <BulkAssignLeadsDialog
      v-model="showBulkAssignDialog"
      :campaign-id="bulkAssignCampaignId"
      :leads="bulkAssignLeads"
      :agents="bulkAssignAgents"
      :contexts="bulkAssignContexts"
      @saved="onBulkAssignSaved"
      @error="notifyError"
    />

    <div class="tk-shell">
      <header class="tk-toolbar">
        <div class="tk-toolbar__start">
          <div
            v-if="!isHumanAgentUser"
            class="tk-tabs"
            role="tablist"
            aria-label="Campaign scope"
          >
            <button
              type="button"
              class="tk-tabs__tab"
              :class="{ 'tk-tabs__tab--active': activeTab === 'ALL' }"
              role="tab"
              :aria-selected="activeTab === 'ALL'"
              @click="setTab('ALL')"
            >
              All campaigns
            </button>
            <button
              type="button"
              class="tk-tabs__tab"
              :class="{ 'tk-tabs__tab--active': activeTab === 'MINE' }"
              role="tab"
              :aria-selected="activeTab === 'MINE'"
              @click="setTab('MINE')"
            >
              My campaigns
            </button>
          </div>
          <p class="tk-toolbar__count">
            {{ filteredCampaigns.length }}
            {{ filteredCampaigns.length === 1 ? 'campaign' : 'campaigns' }}
          </p>
        </div>
        <div class="tk-toolbar__end">
          <button
            type="button"
            class="tk-btn tk-btn--ghost"
            :disabled="loading || !isSessionReady"
            @click="loadCampaigns"
          >
            <v-icon icon="mdi-refresh" size="16" :class="{ 'tk-spin': loading }" />
            Refresh
          </button>
          <button
            v-if="isAdmin"
            type="button"
            class="tk-btn tk-btn--primary"
            :disabled="!isSessionReady"
            @click="showCreateDialog = true"
          >
            <v-icon icon="mdi-plus" size="16" />
            Create campaign
          </button>
        </div>
      </header>

      <section class="tk-table-wrap" aria-label="Calling campaigns">
        <div
          class="tk-table"
          :class="isMineView ? 'tk-table--mine' : 'tk-table--all'"
        >
          <div class="tk-table__head" aria-hidden="true">
            <template v-if="isMineView">
              <span>Campaign</span>
              <span>Date range</span>
              <span>Distribution</span>
              <span>Handled by</span>
              <span>My progress</span>
              <span>Status</span>
              <span>Actions</span>
            </template>
            <template v-else>
              <span>Campaign</span>
              <span>Date range</span>
              <span>Handled by</span>
              <span>Distribution</span>
              <span>Products</span>
              <span>Leads</span>
              <span>Agents</span>
              <span>Status</span>
              <span>Actions</span>
            </template>
          </div>

          <div class="tk-table__body">
            <div v-if="loading" class="tk-table__loading">
              <div v-for="n in 5" :key="n" class="tk-table__skel" />
            </div>

            <div v-else-if="filteredCampaigns.length" role="list">
              <article
                v-for="campaign in filteredCampaigns"
                :key="campaign.id"
                class="tk-table__row"
                role="listitem"
                tabindex="0"
                @click="openCampaign(campaign)"
                @keydown.enter.prevent="openCampaign(campaign)"
              >
                <div class="tk-table__task">
                  <span class="tk-table__mobile-label">Campaign</span>
                  <div class="tk-table__name-row">
                    <strong class="tk-table__name">{{ campaign.name }}</strong>
                    <!-- The campaign id IS the task_id the public API needs for
                         POST /outbound-calls. It was previously not shown
                         anywhere in the portal, so the only way to get one was
                         to read it out of the URL. .stop keeps the click off
                         the row, which would otherwise open the campaign. -->
                    <button
                      type="button"
                      class="tk-copy-id"
                      :title="`Copy campaign ID (task_id) — ${campaign.id}`"
                      :aria-label="`Copy campaign ID for ${campaign.name}`"
                      @click.stop="copyTaskId(campaign)"
                      @keydown.enter.stop
                    >
                      <v-icon size="13">{{ copiedTaskId === campaign.id ? 'mdi-check' : 'mdi-identifier' }}</v-icon>
                      <span>{{ copiedTaskId === campaign.id ? 'Copied' : 'ID' }}</span>
                    </button>
                  </div>
                  <span
                    v-if="!isMineView && showDescription(campaign)"
                    class="tk-table__desc"
                    :title="campaign.description"
                  >
                    {{ campaign.description }}
                  </span>
                  <span v-if="!isMineView" class="tk-table__meta">
                    <template v-if="campaign.assignedAgentIds.length">
                      {{ campaign.assignedAgentIds.length }}
                      {{ campaign.assignedAgentIds.length === 1 ? 'agent' : 'agents' }}
                    </template>
                    <template v-if="campaign.createdOnLabel !== '—'">
                      <template v-if="campaign.assignedAgentIds.length"> · </template>
                      {{ campaign.createdOnLabel }}
                    </template>
                  </span>
                </div>

                <template v-if="isMineView">
                  <div class="tk-table__cell tk-table__schedule">
                    <span class="tk-table__mobile-label">Date range</span>
                    <span class="tk-table__dates">
                      {{ campaign.startDateLabel }} → {{ campaign.endDateLabel }}
                    </span>
                    <div class="tk-bar tk-bar--schedule" aria-hidden="true">
                      <span :style="{ width: `${dateRangeProgress(campaign)}%` }" />
                    </div>
                  </div>
                  <div class="tk-table__cell">
                    <span class="tk-table__mobile-label">Distribution</span>
                    <span>{{ distributionLabel(campaign.distributionMode) }}</span>
                  </div>
                  <div class="tk-table__cell">
                    <span class="tk-table__mobile-label">Handled by</span>
                    <span
                      class="tk-chip"
                      :class="
                        campaign.handledBy === 'AI_AGENT' ? 'tk-chip--ai' : 'tk-chip--human'
                      "
                    >
                      {{ handledByLabel(campaign.handledBy) }}
                    </span>
                  </div>
                  <div class="tk-table__cell tk-table__progress">
                    <span class="tk-table__mobile-label">My progress</span>
                    <strong class="tk-table__pct">
                      {{ formatPercent(campaign.metrics?.callerPercentage) }}
                    </strong>
                    <div class="tk-bar" aria-hidden="true">
                      <span :style="{ width: `${percentNumber(campaign.metrics?.callerPercentage)}%` }" />
                    </div>
                    <span
                      v-if="campaign.showsAgentBreakdown && ownBreakdown(campaign)"
                      class="tk-table__sub"
                    >
                      {{ ownBreakdown(campaign).progressedLeads ?? 0 }}/{{
                        ownBreakdown(campaign).assignedLeads ?? 0
                      }}
                      leads
                    </span>
                  </div>
                </template>

                <template v-else>
                  <div class="tk-table__cell tk-table__schedule">
                    <span class="tk-table__mobile-label">Date range</span>
                    <span class="tk-table__dates">
                      {{ campaign.startDateLabel }} → {{ campaign.endDateLabel }}
                    </span>
                    <div class="tk-bar tk-bar--schedule" aria-hidden="true">
                      <span :style="{ width: `${dateRangeProgress(campaign)}%` }" />
                    </div>
                  </div>
                  <div class="tk-table__cell">
                    <span class="tk-table__mobile-label">Handled by</span>
                    <span
                      class="tk-chip"
                      :class="
                        campaign.handledBy === 'AI_AGENT' ? 'tk-chip--ai' : 'tk-chip--human'
                      "
                    >
                      {{ handledByLabel(campaign.handledBy) }}
                    </span>
                    <span v-if="campaign.isAiHandled" class="tk-table__sub">
                      Auto-dial {{ campaign.autoDialEnabled ? 'on' : 'off' }}
                    </span>
                    <span
                      v-else-if="campaign.autoDialEnabled"
                      class="tk-table__sub"
                    >
                      Softphone auto-dial on
                    </span>
                    <span
                      v-if="campaign.autoAdoptMatchingLeads === false"
                      class="tk-table__sub"
                    >
                      Auto-adopt off
                    </span>
                    <span
                      v-if="campaign.onlyFutureLeads"
                      class="tk-table__sub"
                    >
                      Future leads only
                    </span>
                  </div>
                  <div class="tk-table__cell">
                    <span class="tk-table__mobile-label">Distribution</span>
                    <span>{{ distributionLabel(campaign.distributionMode) }}</span>
                  </div>
                  <div class="tk-table__cell">
                    <span class="tk-table__mobile-label">Products</span>
                    <span
                      class="tk-table__truncate"
                      :title="campaign.contextNames.join(', ')"
                    >
                      {{ campaign.contextNames.join(', ') || '—' }}
                    </span>
                  </div>
                  <div class="tk-table__cell tk-table__leads">
                    <span class="tk-table__mobile-label">Leads</span>
                    <div class="tk-leads-stat">
                      <strong>{{ campaign.metrics?.completedLeads ?? 0 }}</strong>
                      <span class="tk-leads-stat__sep">/</span>
                      <span
                        class="tk-leads-stat__dialled"
                        title="Dialled at least once, regardless of outcome"
                      >{{ campaign.metrics?.dialledLeads ?? 0 }}</span>
                      <span class="tk-leads-stat__sep">/</span>
                      <span>{{ campaign.metrics?.totalLeads ?? '—' }}</span>
                    </div>
                    <div class="tk-bar tk-bar--leads" aria-hidden="true">
                      <span class="tk-bar--leads__dialled" :style="{ width: `${dialledProgress(campaign)}%` }" />
                      <span :style="{ width: `${leadsProgress(campaign)}%` }" />
                    </div>
                    <span class="tk-table__sub">completed / dialled / total</span>
                  </div>
                  <div class="tk-table__cell tk-table__agents-cell">
                    <span class="tk-table__mobile-label">Agents</span>
                    <button
                      v-if="campaign.agentBreakdown?.length"
                      type="button"
                      class="tk-agents-trigger"
                      :class="{ 'is-open': agentsPanelCampaignId === campaign.id }"
                      @click.stop="toggleAgentsPanel(campaign.id)"
                    >
                      <span class="tk-agents-trigger__icon">
                        <v-icon icon="mdi-account-group-outline" size="15" />
                      </span>
                      <span class="tk-agents-trigger__copy">
                        <strong>
                          {{ campaign.agentBreakdown.length }}
                          {{ campaign.agentBreakdown.length === 1 ? 'agent' : 'agents' }}
                        </strong>
                        <small>View breakdown</small>
                      </span>
                    </button>
                    <span v-else class="tk-table__sub">—</span>
                    <div
                      v-if="agentsPanelCampaignId === campaign.id"
                      class="tk-agents-panel"
                      @click.stop
                    >
                      <div class="tk-agents-panel__head">
                        <div>
                          <strong>Team progress</strong>
                          <p>Assigned vs progressed leads</p>
                        </div>
                        <button
                          type="button"
                          class="tk-agents-panel__close"
                          aria-label="Close"
                          @click="agentsPanelCampaignId = ''"
                        >
                          <v-icon icon="mdi-close" size="14" />
                        </button>
                      </div>
                      <div class="tk-agents-panel__list">
                        <div
                          v-for="agent in campaign.agentBreakdown"
                          :key="agent.id"
                          class="tk-agents-panel__card"
                        >
                          <div class="tk-agents-panel__avatar">
                            {{ agentInitials(agent.agentName || agent.agentId) }}
                          </div>
                          <div class="tk-agents-panel__body">
                            <span class="tk-agents-panel__name">
                              {{ agent.agentName || shortId(agent.agentId) }}
                            </span>
                            <div class="tk-bar tk-bar--agent" aria-hidden="true">
                              <span
                                :style="{
                                  width: `${agentLeadsProgress(agent)}%`,
                                }"
                              />
                            </div>
                            <span class="tk-agents-panel__meta">
                              {{ agent.progressedLeads ?? 0 }} progressed ·
                              {{ agent.assignedLeads ?? 0 }} assigned
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <div class="tk-table__cell tk-table__status">
                  <span class="tk-table__mobile-label">Status</span>
                  <span
                    class="tk-chip"
                    :class="`tk-chip--status-${campaign.statusTone || 'neutral'}`"
                  >
                    <v-progress-circular
                      v-if="campaign.status === 'PROCESSING'"
                      indeterminate
                      size="11"
                      width="2"
                      class="mr-1"
                    />
                    {{
                      campaign.status === 'PROCESSING'
                        ? 'Processing leads…'
                        : campaign.statusLabel
                    }}
                  </span>
                </div>

                <div class="tk-table__cell tk-table__actions" @click.stop @keydown.stop>
                  <span class="tk-table__mobile-label">Actions</span>
                  <v-menu location="bottom end" offset="8" :close-on-content-click="true">
                    <template #activator="{ props: menuProps }">
                      <button
                        v-bind="menuProps"
                        type="button"
                        class="tk-actions-trigger"
                        aria-label="Campaign actions"
                        :disabled="campaign.status === 'PROCESSING'"
                      >
                        <v-icon icon="mdi-dots-vertical" size="18" />
                      </button>
                    </template>
                    <div class="tk-actions-menu">
                      <button
                        type="button"
                        class="tk-actions-menu__item"
                        @click="openCampaign(campaign)"
                      >
                        <span class="tk-actions-menu__icon tk-actions-menu__icon--view">
                          <v-icon icon="mdi-eye-outline" size="18" />
                        </span>
                        <span class="tk-actions-menu__text">
                          <span class="tk-actions-menu__title">Open campaign</span>
                          <span class="tk-actions-menu__desc">View leads and progress</span>
                        </span>
                      </button>
                      <button
                        v-if="canEditCampaign(campaign)"
                        type="button"
                        class="tk-actions-menu__item"
                        @click="openEditCampaign(campaign)"
                      >
                        <span class="tk-actions-menu__icon tk-actions-menu__icon--edit">
                          <v-icon icon="mdi-pencil-outline" size="18" />
                        </span>
                        <span class="tk-actions-menu__text">
                          <span class="tk-actions-menu__title">Edit campaign</span>
                          <span class="tk-actions-menu__desc">
                            Update details, filters, and distribution
                          </span>
                        </span>
                      </button>
                      <button
                        v-if="canPauseCampaign(campaign) || canResumeCampaign(campaign)"
                        type="button"
                        class="tk-actions-menu__item"
                        :disabled="statusChangeId === campaign.campaignId"
                        @click="onPauseResumeClick(campaign)"
                      >
                        <span class="tk-actions-menu__icon tk-actions-menu__icon--edit">
                          <v-icon
                            :icon="
                              canResumeCampaign(campaign)
                                ? 'mdi-play-circle-outline'
                                : 'mdi-pause-circle-outline'
                            "
                            size="18"
                          />
                        </span>
                        <span class="tk-actions-menu__text">
                          <span class="tk-actions-menu__title">
                            {{ canResumeCampaign(campaign) ? 'Resume campaign' : 'Pause campaign' }}
                          </span>
                          <span class="tk-actions-menu__desc">
                            {{
                              canResumeCampaign(campaign)
                                ? 'Start calling again with the same settings'
                                : 'Stop calling now — settings are kept'
                            }}
                          </span>
                        </span>
                      </button>
                      <button
                        v-if="canDeleteCampaign(campaign)"
                        type="button"
                        class="tk-actions-menu__item tk-actions-menu__item--danger"
                        :disabled="deletingId === campaign.campaignId"
                        @click="openCampaignConfirm('delete', campaign)"
                      >
                        <span class="tk-actions-menu__icon tk-actions-menu__icon--edit">
                          <v-icon icon="mdi-trash-can-outline" size="18" />
                        </span>
                        <span class="tk-actions-menu__text">
                          <span class="tk-actions-menu__title">Delete campaign</span>
                          <span class="tk-actions-menu__desc">
                            Removes this campaign and its history — leads are kept
                          </span>
                        </span>
                      </button>
                      <button
                        v-if="canBulkAssign(campaign)"
                        type="button"
                        class="tk-actions-menu__item"
                        :disabled="bulkAssignLoading"
                        @click="openBulkAssign(campaign)"
                      >
                        <span class="tk-actions-menu__icon tk-actions-menu__icon--assign">
                          <v-icon icon="mdi-account-multiple-plus-outline" size="18" />
                        </span>
                        <span class="tk-actions-menu__text">
                          <span class="tk-actions-menu__title">Assign leads</span>
                          <span class="tk-actions-menu__desc">
                            Bulk-assign leads to agents (Manual)
                          </span>
                        </span>
                      </button>
                    </div>
                  </v-menu>
                </div>
              </article>
            </div>

            <div v-else-if="isSearchActive" class="tk-empty">
              <v-icon icon="mdi-magnify-close" size="40" />
              <p class="tk-empty__title">No matching campaigns</p>
              <p class="tk-empty__sub">Try another search or clear the app bar filter.</p>
            </div>

            <div v-else class="tk-empty">
              <v-icon icon="mdi-clipboard-list-outline" size="40" />
              <p class="tk-empty__title">
                {{ isMineView ? 'No campaigns assigned to you yet' : 'No campaigns yet' }}
              </p>
              <p class="tk-empty__sub">
                {{
                  isMineView
                    ? 'Campaigns assigned to you will appear here.'
                    : 'Create a campaign to queue leads from your products for AI or human calling.'
                }}
              </p>
              <button
                v-if="isAdmin"
                type="button"
                class="tk-btn tk-btn--primary"
                @click="showCreateDialog = true"
              >
                <v-icon icon="mdi-plus" size="16" />
                Create campaign
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import CreateCampaignDialog from '@/components/Campaigns/CreateCampaignDialog.vue'
import CampaignConfirmDialog from '@/components/Campaigns/CampaignConfirmDialog.vue'
import BulkAssignLeadsDialog from '@/components/Campaigns/BulkAssignLeadsDialog.vue'
import {
  fetchCampaigns,
  fetchMyCampaigns,
  fetchCampaignLeads,
  setCampaignStatus,
  deleteCampaign,
} from '@/api/campaignsApi'
import { fetchOrgUsers } from '@/api/usersApi'
import { fetchContexts } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import {
  normalizeCampaignsList,
  normalizeCampaignLeadsList,
  campaignHandledByLabel,
  campaignDistributionLabel,
  formatCampaignPercent,
  findOwnAgentBreakdown,
} from '@/utils/campaigns'
import { normalizeContextsList } from '@/utils/contexts'
import { normalizeUserRecord } from '@/utils/session'
import { isAiAgent, resolveListAgentType } from '@/utils/orgUsers'
import '@/styles/campaigns/campaigns.css'

defineOptions({ name: 'CampaignsPage' })

const store = useStore()
const router = useRouter()
const route = useRoute()

const snackbar = ref({})
const campaigns = ref([])
const loading = ref(false)
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const editingCampaign = ref(null)
const showBulkAssignDialog = ref(false)
const bulkAssignCampaignId = ref('')
const bulkAssignLeads = ref([])
const bulkAssignAgents = ref([])
const bulkAssignContexts = ref([])
const bulkAssignLoading = ref(false)
// Which campaign is mid pause/resume -- disables just that row's button, so a
// double-click cannot fire the toggle twice against the same campaign.
const statusChangeId = ref('')
const deletingId = ref('')
const agentsPanelCampaignId = ref('')
let processingPollTimer = null

const PROCESSING_POLL_MS = 5000

const isSessionReady = computed(() => store.getters.isSessionReady)
const tableSearch = computed(() => store.state.tableSearch)
const currentUserId = computed(() => String(store.state.currentUserId ?? '').trim())

const isAdmin = computed(() => {
  const role = String(store.state.userRole ?? '').trim().toUpperCase()
  return role === 'ADMIN' || role === 'OWNER'
})

const isHumanAgentUser = computed(() => {
  const role = String(store.state.userRole ?? '').trim().toUpperCase()
  if (role !== 'AGENT') return false
  const type = String(store.state.agentType ?? '').trim().toUpperCase()
  return type !== 'AI_AGENT'
})

const activeTab = ref(isAdmin.value ? 'ALL' : 'MINE')

const isMineView = computed(
  () => isHumanAgentUser.value || activeTab.value === 'MINE',
)

function setTab(tab) {
  if (activeTab.value === tab) return
  activeTab.value = tab
  agentsPanelCampaignId.value = ''
  syncScopeQuery(tab)
  void loadCampaigns()
}

function syncScopeQuery(tab = activeTab.value) {
  const scope = tab === 'MINE' || isHumanAgentUser.value ? 'mine' : 'all'
  const current = String(route.query.scope ?? '').trim().toLowerCase()
  if (current === scope) return
  router.replace({
    name: 'Campaigns',
    query: { ...route.query, scope },
  })
}

const isSearchActive = computed(() =>
  Boolean(String(tableSearch.value || '').trim()),
)

const SEARCH_FIELDS = [
  'name',
  'description',
  'statusLabel',
  'handledBy',
  'distributionMode',
]

const filteredCampaigns = computed(() => {
  const q = String(tableSearch.value || '').trim().toLowerCase()
  if (!q) return campaigns.value
  return campaigns.value.filter(
    (campaign) =>
      SEARCH_FIELDS.some((field) =>
        String(campaign[field] ?? '').toLowerCase().includes(q),
      ) ||
      campaign.contextNames.some((name) => name.toLowerCase().includes(q)),
  )
})

function handledByLabel(value) {
  return campaignHandledByLabel(value)
}

function distributionLabel(value) {
  return campaignDistributionLabel(value)
}

function formatPercent(value) {
  return formatCampaignPercent(value)
}

function ownBreakdown(campaign) {
  return findOwnAgentBreakdown(campaign?.agentBreakdown, currentUserId.value)
}

function shortId(id) {
  const text = String(id ?? '').trim()
  if (!text) return 'Agent'
  return text.length > 8 ? `${text.slice(0, 8)}…` : text
}

function toggleAgentsPanel(campaignId) {
  agentsPanelCampaignId.value = agentsPanelCampaignId.value === campaignId ? '' : campaignId
}

function agentInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase()
}

function parseCampaignDateMs(value) {
  if (value == null || value === '') return null
  const text = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const d = new Date(`${text.slice(0, 10)}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d.getTime()
  }
  const asNum = Number(text)
  if (Number.isFinite(asNum) && asNum > 0) return asNum
  const d = new Date(text)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

function dateRangeProgress(campaign) {
  const start = parseCampaignDateMs(campaign?.startDate)
  const end = parseCampaignDateMs(campaign?.endDate)
  if (start == null || end == null || end <= start) return 0
  const now = Date.now()
  if (now <= start) return 0
  if (now >= end) return 100
  return Math.round(((now - start) / (end - start)) * 100)
}

function leadsProgress(campaign) {
  const total = Number(campaign?.metrics?.totalLeads)
  const done = Number(campaign?.metrics?.completedLeads)
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(done)) return 0
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)))
}

// Separate from leadsProgress -- dialled is a superset of completed (a
// lead can be dialled without picking up), so this renders as its own
// lighter bar segment behind the completed one, not a replacement for it.
function dialledProgress(campaign) {
  const total = Number(campaign?.metrics?.totalLeads)
  const dialled = Number(campaign?.metrics?.dialledLeads)
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(dialled)) return 0
  return Math.max(0, Math.min(100, Math.round((dialled / total) * 100)))
}

function agentLeadsProgress(agent) {
  const total = Number(agent?.assignedLeads)
  const done = Number(agent?.progressedLeads)
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(done)) return 0
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)))
}

function showDescription(campaign) {
  const desc = String(campaign?.description ?? '').trim()
  if (!desc) return false
  return desc.toLowerCase() !== String(campaign?.name ?? '').trim().toLowerCase()
}

function percentNumber(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 0
  const pct = num > 0 && num <= 1 ? num * 100 : num
  return Math.max(0, Math.min(100, pct))
}

function showSnack(type, message) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor:
      type === 'error' ? 'error' : type === 'info' ? 'info' : 'success',
    SnackbarText: message,
  }
}

function notifyError(message) {
  showSnack('error', message)
}

// Copies the campaign's id, which is the task_id the public API's
// POST /outbound-calls requires. Falls back to a visible error rather than
// failing silently -- clipboard writes are blocked outside a secure context
// and in some embedded browsers, and a copy button that does nothing is worse
// than one that says why.
const copiedTaskId = ref('')
let copiedResetTimer = null
async function copyTaskId(campaign) {
  const id = String(campaign?.id ?? '').trim()
  if (!id) return
  try {
    await navigator.clipboard.writeText(id)
    copiedTaskId.value = id
    showSnack('success', 'Campaign ID copied — use it as task_id in the API.')
    if (copiedResetTimer) window.clearTimeout(copiedResetTimer)
    copiedResetTimer = window.setTimeout(() => {
      copiedTaskId.value = ''
    }, 2000)
  } catch {
    showSnack('error', `Couldn't copy automatically. Campaign ID: ${id}`)
  }
}

async function loadCampaigns({ quiet = false } = {}) {
  if (!isSessionReady.value) return

  const tab = activeTab.value
  if (!quiet) loading.value = true
  try {
    const response = tab === 'ALL' ? await fetchCampaigns() : await fetchMyCampaigns()
    if (tab !== activeTab.value) return

    if (!isApiSuccess(response)) {
      if (!quiet) {
        campaigns.value = []
        showSnack('error', getApiMessage(response, 'Failed to load campaigns'))
      }
      return
    }
    campaigns.value = normalizeCampaignsList(unwrapApiList(response))
    scheduleProcessingPoll()
  } finally {
    if (!quiet) loading.value = false
  }
}

function stopProcessingPoll() {
  if (processingPollTimer) {
    clearTimeout(processingPollTimer)
    processingPollTimer = null
  }
}

function scheduleProcessingPoll() {
  stopProcessingPoll()
  const hasProcessing = campaigns.value.some(
    (campaign) => String(campaign.status ?? '').toUpperCase() === 'PROCESSING',
  )
  if (!hasProcessing) return

  processingPollTimer = setTimeout(() => {
    void loadCampaigns({ quiet: true })
  }, PROCESSING_POLL_MS)
}

function openCampaign(campaign) {
  if (!campaign?.campaignId) return
  if (String(campaign.status ?? '').toUpperCase() === 'PROCESSING') {
    showSnack(
      'info',
      'This campaign is still processing lead assignments. It will become Active shortly.',
    )
  }
  router.push({
    name: 'CampaignDetail',
    params: { campaignId: campaign.campaignId },
    query: { leads: isMineView.value ? 'mine' : 'all' },
  })
}

function canEditCampaign(campaign) {
  if (isAdmin.value) return true
  const creator = String(campaign?.createdBy ?? '').trim()
  return Boolean(creator && creator === currentUserId.value)
}

// The org's own default-inbound campaign is what answers calls landing on
// its number -- pausing or deleting it (previously offered here with no
// guard anywhere, frontend or backend) would silently break inbound calls.
// Backend now rejects both regardless, but hiding the actions here means
// nobody discovers that the hard way by clicking them first.
function isDefaultInboundCampaign(campaign) {
  const campaignId = String(campaign?.campaignId ?? '').trim()
  const defaultId = String(store.state.organization?.default_incoming_task_id ?? '').trim()
  return Boolean(campaignId) && campaignId === defaultId
}

function canBulkAssign(campaign) {
  return (
    canEditCampaign(campaign) &&
    String(campaign?.distributionMode ?? '').toUpperCase() === 'MANUAL'
  )
}

// Only a running campaign can be paused and only a paused one resumed --
// EXPIRED, PROCESSING and COMPLETED are computed from dates and upload state,
// so offering the action on those would promise something the backend refuses.
function canPauseCampaign(campaign) {
  return (
    canEditCampaign(campaign) &&
    !isDefaultInboundCampaign(campaign) &&
    String(campaign?.status ?? '').toUpperCase() === 'ACTIVE'
  )
}

function canResumeCampaign(campaign) {
  return (
    canEditCampaign(campaign) &&
    String(campaign?.status ?? '').toUpperCase() === 'PAUSED'
  )
}

// Resume is the reversible, low-risk side of the pause/resume toggle --
// starts calling again with the exact settings it paused with -- so it
// stays instant, same as before. Pause routes through the confirm dialog
// below instead: it has real business impact (no further calls at all)
// and previously had no confirmation whatsoever.
async function onPauseResumeClick(campaign) {
  if (canResumeCampaign(campaign)) {
    await resumeCampaign(campaign)
    return
  }
  if (canPauseCampaign(campaign)) {
    openCampaignConfirm('pause', campaign)
  }
}

async function resumeCampaign(campaign) {
  const campaignId = String(campaign?.campaignId ?? '').trim()
  if (!campaignId || statusChangeId.value) return

  statusChangeId.value = campaignId
  try {
    const response = await setCampaignStatus({ campaignId, status: 'ACTIVE' })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response) || 'Could not resume the campaign.')
      return
    }
    showSnack('success', `"${campaign.name || 'Campaign'}" resumed — calling will start again shortly.`)
    await loadCampaigns({ quiet: true })
  } catch (error) {
    showSnack('error', error?.message || 'Could not resume the campaign.')
  } finally {
    statusChangeId.value = ''
  }
}

// PAUSED, EXPIRED and COMPLETED only, matching the backend -- all three are
// terminal/not-running states with no live dialer against them. Deleting a
// still-ACTIVE campaign would race its own dialer, and offering the action
// on one would promise something the backend refuses.
function canDeleteCampaign(campaign) {
  return (
    canEditCampaign(campaign) &&
    !isDefaultInboundCampaign(campaign) &&
    ['PAUSED', 'EXPIRED', 'COMPLETED'].includes(String(campaign?.status ?? '').toUpperCase())
  )
}

// Shared confirm dialog for both pause and delete -- see CampaignConfirmDialog.vue.
const confirmDialogOpen = ref(false)
const confirmDialogAction = ref('delete')
const confirmDialogCampaign = ref(null)

function openCampaignConfirm(action, campaign) {
  confirmDialogAction.value = action
  confirmDialogCampaign.value = campaign
  confirmDialogOpen.value = true
}

async function confirmDialogFn(campaign) {
  const campaignId = String(campaign?.campaignId ?? '').trim()
  if (!campaignId) return { status: 'ERROR', status_message: 'Missing campaign id.' }
  if (confirmDialogAction.value === 'pause') {
    statusChangeId.value = campaignId
    try {
      return await setCampaignStatus({ campaignId, status: 'PAUSED' })
    } finally {
      statusChangeId.value = ''
    }
  }
  deletingId.value = campaignId
  try {
    return await deleteCampaign({ campaignId })
  } finally {
    deletingId.value = ''
  }
}

async function onConfirmDialogConfirmed(message) {
  showSnack('success', message)
  await loadCampaigns({ quiet: true })
}

function openEditCampaign(campaign) {
  if (!campaign?.campaignId || !canEditCampaign(campaign)) return
  editingCampaign.value = campaign
  showEditDialog.value = true
}

async function openBulkAssign(campaign) {
  if (!canBulkAssign(campaign)) return
  const campaignId = String(campaign.campaignId ?? '').trim()
  if (!campaignId) return

  bulkAssignLoading.value = true
  try {
    const [leadsResponse, usersResponse, contextsResponse] = await Promise.all([
      fetchCampaignLeads({ task_id: campaignId, all_leads: true }),
      fetchOrgUsers({}),
      fetchContexts({}),
    ])

    if (!isApiSuccess(leadsResponse)) {
      showSnack('error', getApiMessage(leadsResponse, 'Failed to load campaign leads'))
      return
    }

    const leadsPayload = leadsResponse?.data
    const leadItems = Array.isArray(leadsPayload?.items)
      ? leadsPayload.items
      : unwrapApiList(leadsResponse)
    bulkAssignLeads.value = normalizeCampaignLeadsList(leadItems)

    const allUsers = isApiSuccess(usersResponse)
      ? unwrapApiList(usersResponse).map(normalizeUserRecord).filter(Boolean)
      : []
    const handledBy = String(campaign.handledBy ?? '').toUpperCase()
    const assignedIds = new Set(
      (Array.isArray(campaign.assignedAgentIds) ? campaign.assignedAgentIds : []).map(
        (id) => String(id ?? '').trim(),
      ),
    )
    bulkAssignAgents.value = allUsers
      .filter((user) => String(user.role ?? '').trim().toUpperCase() === 'AGENT')
      .filter((user) =>
        handledBy === 'AI_AGENT'
          ? isAiAgent(resolveListAgentType(user))
          : !isAiAgent(resolveListAgentType(user)),
      )
      .filter((user) => !assignedIds.size || assignedIds.has(String(user.id)))

    const contextNames = new Set(
      (Array.isArray(campaign.contextNames) ? campaign.contextNames : [])
        .map((name) => String(name ?? '').trim().toLowerCase())
        .filter(Boolean),
    )
    const allContexts = isApiSuccess(contextsResponse)
      ? normalizeContextsList(unwrapApiList(contextsResponse))
      : []
    bulkAssignContexts.value = contextNames.size
      ? allContexts.filter((context) =>
          contextNames.has(
            String(context.context_name ?? context.contextName ?? '')
              .trim()
              .toLowerCase(),
          ),
        )
      : allContexts

    bulkAssignCampaignId.value = campaignId
    showBulkAssignDialog.value = true
  } finally {
    bulkAssignLoading.value = false
  }
}

function onCampaignCreated(message) {
  showSnack('success', message)
  void loadCampaigns()
}

// Fires right as the dialog is about to close after a genuinely new
// campaign is done (not on an edit, not on an error) -- land straight on
// its Settings tab, since that's now where everything the trimmed create
// dialog no longer asks up front actually lives.
function onCampaignCreatedGoToSettings(taskId) {
  if (!taskId) return
  router.push({
    name: 'CampaignDetail',
    params: { campaignId: taskId },
    query: { tab: 'settings' },
  })
}

function onCampaignEdited(message) {
  showSnack('success', typeof message === 'string' ? message : 'Campaign updated.')
  showEditDialog.value = false
  editingCampaign.value = null
  void loadCampaigns()
}

function onBulkAssignSaved(result) {
  const message =
    typeof result === 'string'
      ? result
      : result?.message || 'Leads assigned successfully.'
  showSnack(result?.failed ? 'warning' : 'success', message)
  if (!result?.failed) {
    showBulkAssignDialog.value = false
    bulkAssignCampaignId.value = ''
    bulkAssignLeads.value = []
    bulkAssignAgents.value = []
    bulkAssignContexts.value = []
  }
}

watch(
  isSessionReady,
  (ready) => {
    if (!ready) return
    // Role only becomes reliable once the session is loaded.
    const q = String(route.query.scope ?? '').trim().toLowerCase()
    if (isHumanAgentUser.value) {
      activeTab.value = 'MINE'
    } else if (q === 'mine') {
      activeTab.value = 'MINE'
    } else if (q === 'all') {
      activeTab.value = 'ALL'
    } else {
      activeTab.value = isAdmin.value ? 'ALL' : 'MINE'
    }
    syncScopeQuery(activeTab.value)
    void loadCampaigns()
  },
  { immediate: true },
)

onBeforeUnmount(stopProcessingPoll)
</script>
